import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ResumeUpload from '../../components/common/resume/ResumeUpload';
import ResumesContent from '../../components/common/table/ResumesTable';
import { resumeApi } from '../../services/resumeApi';
import { offerLetterApi } from '../../services/offerLetter.service';
import OfferLetterForm from '../../components/common/offer/OfferLetterForm';
import OfferLetterModal from '../../components/common/offer/OfferLetterModal';
import SendOfferDialog from '../../components/common/offer/SendOfferDialog';
import OfferResponseRow from '../../components/common/offer/OfferResponseRow';
import api from '../../services/api';
import { Button, Card, CardHeader, Icon, StatusPill, Table } from '../../ds';
import { Loading } from '../../components/page';
const small = { fontSize: 'var(--fs-caption)', color: 'var(--text-muted)' };
const eyebrow = { margin: '16px 0 10px', fontSize: 'var(--fs-caption)', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' };
// ── Selection Process Report ──────────────────────────────────────────────────
const STATUS_TONE = {
  shortlisted: 'success',
  rejected:    'danger',
  completed:   'info',
  not_started: 'neutral',
  sent:        'info',
  accepted:    'success',
  declined:    'danger',
  lapsed:      'warning',
  generated:   'info',
};
function StageRow({ icon, label, status, score, detail }) {
  const words = status?.replace(/_/g, ' ') || 'Not Started';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'var(--brand-primary-soft)', color: 'var(--brand-primary-ink)', display: 'grid', placeItems: 'center', flex: 'none' }}>
        <Icon name={icon} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 'var(--fs-body-sm)', fontWeight: 600, color: 'var(--text-1)' }}>{label}</p>
        {detail && <p style={{ ...small, margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{detail}</p>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 'none' }}>
        {score > 0 && (
          <span className="num" style={{ fontSize: 'var(--fs-body-sm)', fontWeight: 600, color: 'var(--text-1)' }}>{Math.round(score)}%</span>
        )}
        <StatusPill tone={STATUS_TONE[status] || 'neutral'}>{words.charAt(0).toUpperCase() + words.slice(1)}</StatusPill>
      </div>
    </div>
  );
}
// Map pipeline stage → resume score/status fields (for legacy rounds)
const ROUND_FIELD = {
  preScreening:     { score: 'preScreeningScore',    status: 'preScreeningStatus',    feedback: r => r.preScreeningResult?.feedback },
  psychoanalytical: { score: 'psychoanalyticalScore', status: 'psychoanalyticalStatus', feedback: r => r.psychoanalyticalResult?.feedback },
  finalInterview:   { score: 'finalInterviewScore',  status: 'finalInterviewStatus',  feedback: r => r.finalInterviewResult?.feedback },
  coding:           { score: 'codingScore',           status: 'codingStatus',           feedback: () => null },
};
function AICostSection({ resumeId }) {
  const [costs, setCosts]       = useState(null);
  const [loading, setLoading]   = useState(false);
  const [loaded, setLoaded]     = useState(false);
  useEffect(() => {
    if (!resumeId || loaded) return;
    setLoading(true);
    api.get(`/reports/candidate/${resumeId}/costs`)
      .then(r => { setCosts(r.data?.data || null); setLoaded(true); })
      .catch(() => { setCosts(null); setLoaded(true); })
      .finally(() => setLoading(false));
  }, [resumeId, loaded]);
  if (loading) return <Loading label="Loading AI cost data" />;
  if (!costs || (costs.totalUsd === 0 && costs.totalTokens === 0)) return null;
  const figure = (label, value) => (
    <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 12, textAlign: 'center' }}>
      <div style={small}>{label}</div>
      <div className="num" style={{ fontSize: 'var(--fs-body-lg)', fontWeight: 600, color: 'var(--text-1)', marginTop: 2 }}>{value}</div>
    </div>
  );
  const rows = [
    ...(costs.byStage || []).map((s, i) => ({ id: `s${i}`, stage: s.stage, ops: s.count, tokens: s.tokens, usd: s.usdCost })),
    { id: 'total', total: true, stage: 'Total', ops: costs.timeline?.length || 0, tokens: costs.totalTokens, usd: costs.totalUsd },
  ];
  const strong = (r, v) => <span className="num" style={{ fontWeight: r.total ? 600 : 400, color: r.total ? 'var(--text-1)' : undefined }}>{v}</span>;
  return (
    <div>
      <p style={eyebrow}>Total AI Cost Incurred</p>
      {/* Grand total row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, marginBottom: 12 }}>
        {figure('Total Cost', `$${costs.totalUsd.toFixed(4)}`)}
        {figure('Total Tokens', costs.totalTokens.toLocaleString())}
        {figure('AI Operations', costs.timeline?.length || 0)}
      </div>
      {/* Per-stage breakdown */}
      {costs.byStage?.length > 0 && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
          <Table
            columns={[
              { key: 'stage', label: 'Stage', render: (r) => <span style={{ fontWeight: r.total ? 600 : 500, color: 'var(--text-1)' }}>{r.stage}</span> },
              { key: 'ops', label: 'Ops', render: (r) => strong(r, r.ops) },
              { key: 'tokens', label: 'Tokens', render: (r) => strong(r, r.tokens.toLocaleString()) },
              { key: 'usd', label: 'Cost (USD)', render: (r) => strong(r, `$${r.usd.toFixed(4)}`) },
            ]}
            rows={rows}
            getRowId={(r) => r.id}
          />
        </div>
      )}
    </div>
  );
}
/* Handing an accepted candidate to People (HIR-HR6): what happened, in words, and a retry when it
   needs one. People owns the employee from then on — corrections happen there, not here. */
const HANDOVER_LOOK = {
  done:        ['success', 'Added to People'],
  exists:      ['warning', 'Already in People'],
  needs_info:  ['warning', 'Needs a detail before they can be added to People'],
  failed:      ['danger', 'Could not be added to People'],
  in_progress: ['neutral', 'Being added to People…'],
};
// @story HIR-HR6
function HandoverRow({ resume }) {
  const h = resume.handover;
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [tone, label] = HANDOVER_LOOK[h?.status] || ['neutral', 'Not yet added to People'];
  const canRetry = !h?.status || h.status === 'failed' || h.status === 'needs_info';
  // Their CV, photo and signature follow the person into People. A failure here never undoes
  // the person; it has its own retry, and People ignores a document it already has.
  const docs = h?.status === 'done' ? h.documents : null;
  const docsFailed = docs?.status === 'failed';
  const docsLabel = !docs?.status ? null
    : docsFailed ? `Documents: ${docs.sent || 0} of ${docs.total || '?'} sent to People`
      : docs.total ? `Documents sent to People (${docs.sent} of ${docs.total})` : 'No documents to send';
  const retry = async () => {
    setBusy(true); setNote('');
    try {
      const r = await offerLetterApi.retryHandover(resume._id);
      const d = r?.data?.documents;
      setNote(r?.data?.error || d?.error
        || (r?.data?.status === 'done' || d?.status === 'done' ? 'Sent to People — refresh to see it.' : r?.message || 'Done.'));
    } catch (e) {
      setNote(e?.message || 'Could not run the handover.');
    } finally { setBusy(false); }
  };
  return (
    <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 12, rowGap: 6, fontSize: 'var(--fs-body-sm)' }}>
      <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>People</span>
      <StatusPill tone={tone}>{label}{h?.employeeCode ? ` · ${h.employeeCode}` : ''}</StatusPill>
      {h?.error && h.status !== 'done' && <span style={small}>{h.error}</span>}
      {canRetry && (
        <Button size="sm" variant="secondary" onClick={retry} disabled={busy}>
          {busy ? 'Working…' : 'Add to People'}
        </Button>
      )}
      {docsLabel && <span style={{ ...small, color: docsFailed ? 'var(--status-danger-text)' : small.color }}>{docsLabel}</span>}
      {docsFailed && docs.error && <span style={small}>{docs.error}</span>}
      {docsFailed && (
        <Button size="sm" variant="secondary" onClick={retry} disabled={busy}>
          {busy ? 'Working…' : 'Send documents again'}
        </Button>
      )}
      {note && <span style={small}>{note}</span>}
    </div>
  );
}
function CandidateReport({ resume, pipelineStages, onChanged }) {
  const [open, setOpen] = useState(false);
  // Build the stages to show: use configured pipeline, fall back to nothing
  const stagesToShow = pipelineStages || [];
  // Collect scores for average
  const allScores = [
    resume.aiScore,
    ...stagesToShow.map(s => {
      const psr = (resume.pipelineStageResults || []).find(p => p.stageId === s.stageId);
      if (psr) return psr.score;
      const rf = ROUND_FIELD[s.round];
      return rf ? resume[rf.score] : null;
    }),
  ].filter(s => s > 0);
  const avgScore = allScores.length ? Math.round(allScores.reduce((a, b) => a + b, 0) / allScores.length) : null;
  // Highest stage reached
  const lastPassed = [...stagesToShow].reverse().find(s => {
    const psr = (resume.pipelineStageResults || []).find(p => p.stageId === s.stageId);
    if (psr) return psr.status === 'shortlisted';
    const rf = ROUND_FIELD[s.round];
    return rf && resume[rf.status] === 'shortlisted';
  });
  const highestStage = resume.offerLetterStatus !== 'not_generated' ? 'Offer'
    : lastPassed?.label || (resume.resumeScreeningStatus === 'shortlisted' ? 'Resume Screening' : 'Applied');
  const outcome = resume.offerLetterStatus === 'accepted' ? '✓ Hired'
    : (resume.offerLetterStatus === 'rejected' || resume.offerLetterStatus === 'declined') ? '✗ Offer Declined'
    : resume.offerLetterStatus === 'lapsed' ? '✗ Offer Lapsed'
    : lastPassed ? 'In Progress'
    : '✗ Not Selected';
  const outcomeTone = outcome.startsWith('✓') ? 'success' : outcome.startsWith('✗') ? 'danger' : 'neutral';
  return (
    <Card padding="0">
      <button onClick={() => setOpen(o => !o)} aria-expanded={open}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 20px', background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', textAlign: 'left' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div aria-hidden="true" style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--brand-primary-soft)', color: 'var(--brand-primary-ink)', display: 'grid', placeItems: 'center', fontWeight: 600, flex: 'none' }}>
            {(resume.candidateName || 'C')[0].toUpperCase()}
          </div>
          <div>
            <p style={{ margin: 0, fontWeight: 600, color: 'var(--text-1)' }}>{resume.candidateName || 'Unknown'}</p>
            <p style={{ ...small, margin: 0 }}>{resume.email}</p>
          </div>
          <StatusPill tone="info" dot={false}>{highestStage}</StatusPill>
          {avgScore && (
            <StatusPill tone="neutral" dot={false}><Icon name="medal" size={12} /> Avg {avgScore}%</StatusPill>
          )}
        </div>
        <Icon name="caret-down" style={{ color: 'var(--text-muted)', flex: 'none', transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>
      {open && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '0 20px 20px' }}>
          <p style={eyebrow}>Selection Journey</p>
          {/* Always show resume screening first */}
          <StageRow icon="file-text" label="Resume Screening"
            status={resume.resumeScreeningStatus} score={resume.aiScore}
            detail={resume.fileName} />
          {/* Show only the configured pipeline stages */}
          {stagesToShow.length === 0 && (
            <p style={{ ...small, fontSize: 'var(--fs-body-sm)', padding: '12px 0', textAlign: 'center', fontStyle: 'italic', margin: 0 }}>No assessment pipeline configured for this JD.</p>
          )}
          {stagesToShow.map((stage, i) => {
            // Try pipelineStageResults first (custom stage with own score)
            const psr = (resume.pipelineStageResults || []).find(p => p.stageId === stage.stageId);
            if (psr) {
              return <StageRow key={i} icon="medal" label={stage.label}
                status={psr.status} score={psr.score}
                detail={`Pass score: ${stage.passingScore}%`} />;
            }
            // Fall back to round fields for legacy-mapped stages
            const rf = ROUND_FIELD[stage.round];
            if (rf) {
              return <StageRow key={i} icon="chat-circle-text" label={stage.label}
                status={resume[rf.status]} score={resume[rf.score]}
                detail={rf.feedback(resume) || `Pass score: ${stage.passingScore}%`} />;
            }
            return null;
          })}
          {/* Offer letter */}
          <StageRow icon="handshake" label="Offer Letter"
            status={resume.offerLetterStatus} score={null}
            detail={resume.offerLetterStatus !== 'not_generated' ? `Offer ${resume.offerLetterStatus}` : null} />
          {/* HIR-HR5: reply-by date, the answer and who recorded it */}
          <OfferResponseRow resume={resume} onChanged={onChanged} />
          {resume.offerLetterStatus === 'accepted' && <HandoverRow resume={resume} />}
          {/* Summary */}
          <div style={{ marginTop: 16, padding: 16, background: 'var(--surface-2)', borderRadius: 'var(--radius-md)' }}>
            <p style={{ ...eyebrow, margin: '0 0 8px' }}>Summary</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)' }}>
              <span>Assessments: <strong style={{ color: 'var(--text-1)' }}>{stagesToShow.length}</strong></span>
              {avgScore && <span>Avg score: <strong style={{ color: 'var(--text-1)' }}>{avgScore}%</strong></span>}
              <span>Reached: <strong style={{ color: 'var(--text-1)' }}>{highestStage}</strong></span>
              <StatusPill tone={outcomeTone}>{outcome}</StatusPill>
            </div>
          </div>
          {/* AI Cost — lazy-loaded when the card expands */}
          <AICostSection resumeId={resume._id} />
        </div>
      )}
    </Card>
  );
}
function SelectionReports({ resumes, pipelineStages, onChanged }) {
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 'var(--fs-display-sm)', fontWeight: 600, color: 'var(--text-1)' }}>Candidate Selection Reports</h3>
        <StatusPill tone="neutral" dot={false}>{resumes.length}</StatusPill>
        {pipelineStages.length > 0 && (
          <StatusPill tone="info" dot={false}>
            {pipelineStages.length} assessment stage{pipelineStages.length !== 1 ? 's' : ''}
          </StatusPill>
        )}
      </div>
      {resumes.map(r => <CandidateReport key={r._id} resume={r} pipelineStages={pipelineStages} onChanged={onChanged} />)}
    </div>
  );
}
// @story HIR-HR5
const OfferLetterPage = () => {
  const { id: jobId } = useParams();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [hasTemplate, setHasTemplate] = useState(false);
  const [templateInfo, setTemplateInfo] = useState(null);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generatingOffer, setGeneratingOffer] = useState(null);
  // New form + viewer state
  const [showFormFor, setShowFormFor]   = useState(null); // { resumeId, candidateName }
  const [viewOffer, setViewOffer]       = useState(null); // { content, company, resumeId }
  const [sendFor, setSendFor]           = useState(null); // { resumeId, candidateName } — HIR-HR5 send dialog
  const [checkingTemplate, setCheckingTemplate] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalResumes, setTotalResumes] = useState(0);
  const [currentFilter, setCurrentFilter] = useState('all');
  const [statusCounts, setStatusCounts] = useState({});
  const [filteredResumes, setFilteredResumes] = useState([]);
  // Filter states for each stage — default 'all' so all candidates are visible
  // (candidates on custom pipelines don't have legacy preScreeningStatus etc. set,
  //  so defaulting to 'shortlisted' would silently hide everyone)
  const [preScreeningFilter, setPreScreeningFilter] = useState('all');
  const [psychoanalyticalFilter, setPsychoanalyticalFilter] = useState('all');
  const [finalInterviewFilter, setFinalInterviewFilter] = useState('all');
  // Fetch job details and resumes
  useEffect(() => {
    fetchJobDetails();
  }, [jobId]);
  useEffect(() => {
    if (!checkingTemplate && hasTemplate) {
      fetchShortlistedResumes();
    }
  }, [jobId, currentPage, itemsPerPage, checkingTemplate, hasTemplate]);
  // ── Real-time score polling ──────────────────────────────────────────
  // Poll every 30 s while any candidate still has an interview in the "sent"
  // state (any round) so scores update without manual refresh.
  useEffect(() => {
    const hasPending = resumes.some(r =>
      r.inviteStatus?.preScreening     === 'sent' ||
      r.inviteStatus?.psychoanalytical === 'sent' ||
      r.inviteStatus?.finalInterview   === 'sent' ||
      r.inviteStatus?.coding           === 'sent'
    );
    if (!hasPending) return;
    const timer = setInterval(() => { fetchShortlistedResumes(); }, 30000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumes]);
  // No client-side stage filtering needed — server already returns only
  // candidates shortlisted in the last pipeline stage via 'last_stage_shortlisted'.
  const applyFilters = () => {
    setFilteredResumes([...resumes]);
  };
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchShortlistedResumes();
  };
  const handleFilterChange = (filter) => {
    setCurrentFilter(filter);
    setCurrentPage(1); // Reset to first page when filter changes
    fetchShortlistedResumes();
  };
  const fetchJobDetails = async () => {
    try {
      setCheckingTemplate(true);
      const [jobRes, pipelineRes] = await Promise.all([
        offerLetterApi.getJobDetails(jobId),
        api.get(`/jobs/${jobId}/effective-pipeline`).catch(() => null),
      ]);
      const template = jobRes.data.offerLetterTemplate;
      const templateExists = !!template;
      setHasTemplate(templateExists);
      setTemplateInfo(template || null);
      setShowUploadModal(!templateExists);
      // Store ALL pipeline stages (custom + legacy) so CandidateReport can
      // show the full journey and so we know which is the final qualifying stage.
      const stages = pipelineRes?.data?.data?.stages || [];
      setPipelineStages(stages);
    } catch (error) {
      console.error('Error fetching job details:', error);
      toast.error('Failed to check offer letter template');
    } finally {
      setCheckingTemplate(false);
    }
  };
  const fetchShortlistedResumes = async () => {
    try {
      setLoading(true);
      // Always fetch candidates shortlisted in the last pipeline stage —
      // works for both legacy and custom pipelines.
      const response = await resumeApi.getAllResumes(
        jobId,
        currentPage,
        itemsPerPage,
        'last_stage_shortlisted'
      );
      const fetched = response.data.resumes || [];
      console.log(`[offer-letter] fetched ${fetched.length} last-stage-shortlisted candidates`);
      setResumes(fetched);
      setFilteredResumes(fetched);
      setTotalResumes(response.data.total || fetched.length);
      setStatusCounts(response.data.statusCounts || {});
    } catch (error) {
      console.error('Error fetching resumes:', error);
      toast.error('Failed to fetch candidates');
    } finally {
      setLoading(false);
    }
  };
  const handleTemplateUpload = async (files) => {
    try {
      setLoading(true);
      const file = files[0];
      const formData = new FormData();
      formData.append('template', file);
      formData.append('jobId', jobId);
      await offerLetterApi.uploadTemplate(jobId, formData);
      setHasTemplate(true);
      setShowUploadModal(false);
      toast.success('Template uploaded — offer letters will now use this template');
      // Re-fetch job details so the template info is fresh for generation
      await fetchJobDetails();
    } catch (error) {
      console.error('Error uploading template:', error);
      toast.error('Failed to upload template');
    } finally {
      setLoading(false);
    }
  };
  // Open the form instead of auto-generating
  const onGenerateOffer = (resumeId) => {
    const resume = resumes.find((r) => r._id === resumeId);
    setShowFormFor({ resumeId, candidateName: resume?.candidateName || "Candidate" });
  };
  // Called when the form successfully generates
  const onOfferGenerated = (result) => {
    fetchShortlistedResumes();
    if (result?.offerLetter) {
      setViewOffer({ content: result.offerLetter, company: result.company || null, resumeId: showFormFor?.resumeId || null });
    }
  };
  // Sending asks for the reply-by date first (HIR-HR5) — see SendOfferDialog.
  const onSendOffer = (resumeId) => {
    const resume = resumes.find((r) => r._id === resumeId);
    setSendFor({ resumeId, candidateName: resume?.candidateName || 'Candidate' });
  };
  return (
    <div style={{ display: 'grid', gap: 24, minWidth: 0 }}>
      <Card>
        <CardHeader
          title="Offer Letters"
          action={!checkingTemplate && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {hasTemplate && templateInfo && (
                <StatusPill tone="success">Template: {templateInfo.fileName || 'Uploaded'}</StatusPill>
              )}
              <Button size="sm" icon={<Icon name="file-arrow-up" />} onClick={() => setShowUploadModal(true)}>
                {hasTemplate ? 'Update Template' : 'Upload Template'}
              </Button>
            </div>
          )}
        />
        {checkingTemplate ? (
          <Loading label="Checking offer letter template" />
        ) : hasTemplate && pipelineStages.length > 0 ? (() => {
          const enabledStages = pipelineStages.filter(s => s.enabled !== false);
          const lastStage = enabledStages[enabledStages.length - 1];
          return lastStage ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)' }}>
              <span>Showing candidates <strong style={{ color: 'var(--text-1)' }}>shortlisted</strong> in the final stage:</span>
              <StatusPill tone="success" dot={false}>{lastStage.label}</StatusPill>
              <span style={{ marginLeft: 'auto', ...small }}>({filteredResumes.length} candidate{filteredResumes.length !== 1 ? 's' : ''})</span>
            </div>
          ) : null;
        })() : null}
      </Card>
      {!checkingTemplate && (
        <>
          {showUploadModal && (
            <ResumeUpload
              isOpen={showUploadModal}
              onClose={() => setShowUploadModal(false)}
              onUpload={handleTemplateUpload}
              title="offerLetter"
              accept=".doc,.docx,.pdf"
              maxFiles={1}
            />
          )}
          {hasTemplate && (
            <>
              {loading ? (
                <Card><Loading label="Loading candidates" /></Card>
              ) : (
                <div style={{ minWidth: 0 }}>
                  <ResumesContent
                    resumes={filteredResumes}
                    pagination={{
                      page: currentPage,
                      totalPages: Math.ceil(totalResumes / itemsPerPage),
                      total: totalResumes,
                      limit: itemsPerPage,
                      statusCounts: statusCounts
                    }}
                    setPagination={(newPagination) => {
                      setCurrentPage(newPagination.page);
                      setItemsPerPage(newPagination.limit);
                    }}
                    onPageChange={handlePageChange}
                    currentFilter={currentFilter}
                    onFilterChange={handleFilterChange}
                    screenType="offer-letter"
                    pipelineStages={pipelineStages}
                    onGenerateOffer={onGenerateOffer}
                    onSendOffer={onSendOffer}
                    onStatusChange={async (resumeId, type, newStatus, override) => {
                      try {
                        await resumeApi.updateResumeStatus(resumeId, type, newStatus, override);
                        fetchShortlistedResumes();
                      } catch { /* toast shown by dropdown */ }
                    }}
                    loading={loading}
                    generatingOffer={generatingOffer}
                  />
                </div>
              )}
              {/* Selection Process Reports */}
              {!loading && filteredResumes.length > 0 && (
                <SelectionReports resumes={filteredResumes} pipelineStages={pipelineStages} onChanged={fetchShortlistedResumes} />
              )}
            </>
          )}
        </>
      )}
      {/* Offer form — HR enters CTC + position before generating */}
      {showFormFor && (
        <OfferLetterForm
          isOpen={!!showFormFor}
          onClose={() => setShowFormFor(null)}
          resumeId={showFormFor.resumeId}
          candidateName={showFormFor.candidateName}
          onGenerated={onOfferGenerated}
        />
      )}
      {sendFor && (
        <SendOfferDialog
          resumeId={sendFor.resumeId}
          candidateName={sendFor.candidateName}
          onClose={() => setSendFor(null)}
          onSent={(res) => {
            const d = res?.data?.deadline;
            toast.success(d ? `Offer sent — reply by ${new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' })}` : 'Offer sent');
            fetchShortlistedResumes();
          }}
        />
      )}
      {/* Offer viewer — markdown + logo + printable PDF */}
      {viewOffer && (
        <OfferLetterModal
          isOpen={!!viewOffer}
          onClose={() => setViewOffer(null)}
          content={viewOffer.content}
          company={viewOffer.company}
          resumeId={viewOffer.resumeId}
        />
      )}
    </div>
  );
};
export default OfferLetterPage;
